<template>
  <div class="doctors-page">
    <!-- 专家团队页面横幅 -->
    <section class="doctors-hero">
      <div class="hero-overlay">
        <div class="hero-content">
          <h1 v-for="(item, index) in defaultInfos.doctor_title" :key="index">
            {{ item }}
          </h1>
          <p v-for="(item, index) in defaultInfos.doctor_subtitle" :key="index">
            {{ item }}
          </p>
        </div>
      </div>
    </section>

    <!-- 专家列表 -->
    <section class="doctors-list-section">
      <div class="container">
        <div class="doctors-filter">
          <div class="filter-buttons">
            <template v-for="dept in departments" :key="dept.id">
              <button
                class="filter-btn"
                :class="{ active: selectedDepartment === dept.value }"
                @click="handleFilter(dept.value)"
              >
                {{ dept.label }}
              </button>
            </template>
          </div>
        </div>
        <div class="doctors-grid">
          <div
            class="doctor-card"
            v-for="doctor in filteredDoctors"
            :key="doctor.id"
          >
            <div class="doctor-image">
              <img
                class="doctor-avatar"
                :src="doctor.avatar"
                :alt="doctor.name"
              />
              <div class="doctor-badge" v-if="doctor.badge">
                {{ doctor.badge }}
              </div>
            </div>
            <div class="doctor-info">
              <h3>{{ doctor.name }}</h3>
              <p class="doctor-title">{{ doctor.title }}</p>
              <p class="doctor-specialty">{{ doctor.specialty }}</p>
              <p class="doctor-experience" :title="doctor.experience">
                {{ doctor.experience }}
              </p>
              <div class="doctor-expertise">
                <span
                  :itemid="tag.expertise"
                  :title="tag.expertise"
                  class="expertise-tag"
                  v-for="tag in doctor.expertiseList"
                  :key="tag.sortOrder"
                  >{{ tag.expertise }}</span
                >
              </div>
              <div class="doctor-actions">
                <!-- <button class="btn-primary">预约挂号</button> -->
                <!-- <button class="btn-outline">查看详情</button> -->
              </div>
            </div>
          </div>

          <!-- 空状态提示 -->
          <div
            v-if="!isLoading && filteredDoctors.length === 0"
            class="empty-state"
          >
            <div class="empty-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h3 class="empty-title">暂无医生信息</h3>
          </div>
        </div>
      </div>
    </section>
    <!-- 专家介绍 -->
    <section class="doctors-intro">
      <div class="container">
        <div class="intro-content">
          <div class="intro-text">
            <h2>专家团队优势</h2>
            <p>
              {{ currentTeamInfo?.description }}
            </p>
            <ul>
              <template
                v-for="param in currentTeamInfo?.advantages"
                :key="param.sortOrder"
              >
                <li>{{ param.advantage }}</li>
              </template>
            </ul>
          </div>
          <div class="intro-image">
            <div class="image-placeholder">
              <template
                v-if="
                  currentTeamInfo?.photographs &&
                  currentTeamInfo.photographs.length > 0
                "
              >
                <Carousel
                  :images="currentTeamInfo.photographs"
                  :wh="{ width: '100%', height: '100%' }"
                />
              </template>
              <template v-else>
                <Carousel />
              </template>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import Carousel from "@/components/Carousel/index.vue";
import { useDoctorsListStore, type Doctor } from "@/stores/doctors";
import { useTeamsStore, type teamType } from "@/stores/teams";
import { useDefaultInfoStore } from "@/stores/defaultInfo.ts";
const defaultInfoStore = useDefaultInfoStore();
const defaultInfos = computed(() => defaultInfoStore.getFormateInfo);

// Store 实例
const doctorsListStore = useDoctorsListStore();
const teamListStore = useTeamsStore();

// 路由实例（优先定义）
const route = useRoute();
const router = useRouter();

// 状态管理
const isLoading = ref(true);
const selectedDepartment = ref("all");

const departments = computed(() => doctorsListStore.doctorsCategories);
const currentTeamInfo = computed(() => {
  const deptLabel =
    departments.value.find((d) => d.value === selectedDepartment.value)
      ?.label || "";
  return findTeamInfo(deptLabel);
});
/**
 * 查找对应团队信息
 */
const findTeamInfo = (departmentName: string): teamType | undefined => {
  const teams = teamListStore.getTeamsList;
  return teams.find((team) => team.name === departmentName);
};

// 计算属性：过滤后的医生列表
const filteredDoctors = computed<Doctor[]>(() => {
  if (selectedDepartment.value === "all") {
    return doctorsListStore.majorDoctors;
  }

  if (
    selectedDepartment.value === "medical" ||
    selectedDepartment.value === "laboratory"
  ) {
    return doctorsListStore
      .doctorsByCategory("medical")
      .concat(doctorsListStore.doctorsByCategory("laboratory"));
  }



  return doctorsListStore.doctorsByCategory(selectedDepartment.value);
});

/**
 * 处理筛选按钮点击
 */
const handleFilter = (category: string) => {
  selectedDepartment.value = category;

  // 使用 replace 模式，避免历史记录堆积
  if (category === "all") {
    router.replace("/doctors");
  } else {
    router.replace(`/doctors/${category}`);
  }
};

// 初始化数据
onMounted(async () => {
  try {
    isLoading.value = true;

    // 并行加载数据
    await Promise.all([
      doctorsListStore.fetchDoctorsList(),
      teamListStore.fetchTeamsList(),
    ]);

    // 根据路由参数设置初始筛选条件
    const department = route.params.dept as string;

    if (department) {
      handleFilter(department);
    } else {
      handleFilter("all");
    }
  } catch (error) {
    console.error("加载数据失败:", error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
@import url("./index.css");
</style>
